import { useState, useEffect } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { getClients } from "../../api/clients"
import { getRoutines } from "../../api/routines"
import { getBodyMetrics, createBodyMetric, deleteBodyMetric } from "../../api/bodyMetrics"
import { getWorkoutLogs, createWorkoutLog, deleteWorkoutLog } from "../../api/workoutLogs"
import type { Client, Routine, BodyMetric, WorkoutLog } from "../../types"
import ProgressCharts from "../client/ProgressCharts"
import SessionNotes from "../client/SessionNotes"

function formatDate(d: string) {
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

// ─── Body Metrics Section ─────────────────────────────────────────────────────

function BodyMetricsSection({ clientId }: { clientId: number }) {
  const [metrics, setMetrics] = useState<BodyMetric[]>([])
  const [showForm, setShowForm] = useState(false)
  const [weight, setWeight] = useState("")
  const [bodyFat, setBodyFat] = useState("")
  const [muscleMass, setMuscleMass] = useState("")
  const [notes, setNotes] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getBodyMetrics(clientId)
      .then((data) => setMetrics(Array.isArray(data) ? data : []))
      .catch(() => setMetrics([]))
  }, [clientId])

  async function handleAdd() {
    setSaving(true)
    try {
      const metric = await createBodyMetric(
        {
          recorded_at: new Date().toISOString().split("T")[0],
          weight: weight ? Number(weight) : undefined,
          body_fat: bodyFat ? Number(bodyFat) : undefined,
          muscle_mass: muscleMass ? Number(muscleMass) : undefined,
          notes: notes || undefined,
        },
        clientId,
      )
      setMetrics((prev) => [metric, ...prev])
      setWeight("")
      setBodyFat("")
      setMuscleMass("")
      setNotes("")
      setShowForm(false)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Delete this body metric entry?")) return
    await deleteBodyMetric(id)
    setMetrics((prev) => prev.filter((m) => m.id !== id))
  }

  // Latest values for summary
  const latest = metrics[0]

  return (
    <div className="rounded-xl border border-white/10 bg-surface">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <h3 className="text-sm font-semibold text-white">Body Metrics</h3>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="text-xs text-primary hover:text-primary-hover transition"
        >
          + Record
        </button>
      </div>

      {/* Summary cards */}
      {latest && (
        <div className="grid grid-cols-3 gap-3 px-4 py-3 border-b border-white/5">
          {[
            { label: "Weight", value: latest.weight ? `${latest.weight} kg` : "—" },
            { label: "Body Fat", value: latest.body_fat ? `${latest.body_fat}%` : "—" },
            { label: "Muscle Mass", value: latest.muscle_mass ? `${latest.muscle_mass} kg` : "—" },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-xs text-muted">{s.label}</p>
              <p className="text-lg font-bold text-primary">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className="px-4 py-3 border-b border-white/5 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <input
              type="number"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="Weight (kg)"
              className="rounded-lg border border-white/10 bg-surface-light px-2 py-1.5 text-sm text-white outline-none focus:border-primary"
            />
            <input
              type="number"
              value={bodyFat}
              onChange={(e) => setBodyFat(e.target.value)}
              placeholder="Body fat %"
              className="rounded-lg border border-white/10 bg-surface-light px-2 py-1.5 text-sm text-white outline-none focus:border-primary"
            />
            <input
              type="number"
              value={muscleMass}
              onChange={(e) => setMuscleMass(e.target.value)}
              placeholder="Muscle (kg)"
              className="rounded-lg border border-white/10 bg-surface-light px-2 py-1.5 text-sm text-white outline-none focus:border-primary"
            />
          </div>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            className="w-full rounded-lg border border-white/10 bg-surface-light px-2 py-1.5 text-sm text-white outline-none focus:border-primary"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={saving}
              className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-black hover:bg-primary-hover transition disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="text-xs text-muted hover:text-white transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* History */}
      <div className="max-h-60 overflow-y-auto">
        {metrics.length === 0 ? (
          <p className="px-4 py-4 text-xs text-muted text-center">No body metrics recorded yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-muted border-b border-white/5">
              <tr>
                <th className="px-4 py-2 text-left">Date</th>
                <th className="px-4 py-2">Weight</th>
                <th className="px-4 py-2">Fat%</th>
                <th className="px-4 py-2">Muscle</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((m) => (
                <tr key={m.id} className="border-b border-white/5">
                  <td className="px-4 py-2 text-muted">{formatDate(m.recorded_at)}</td>
                  <td className="px-4 py-2 text-white text-center">{m.weight ?? "—"}</td>
                  <td className="px-4 py-2 text-white text-center">{m.body_fat ?? "—"}</td>
                  <td className="px-4 py-2 text-white text-center">{m.muscle_mass ?? "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => handleDelete(m.id)}
                      className="text-danger hover:text-danger/80"
                    >
                      &times;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// ─── Workout Logs Section ─────────────────────────────────────────────────────

function WorkoutLogsSection({ clientId, routines }: { clientId: number; routines: Routine[] }) {
  const [logs, setLogs] = useState<WorkoutLog[]>([])
  const [showForm, setShowForm] = useState(false)
  const [routineId, setRoutineId] = useState<number>(routines[0]?.id ?? 0)
  const [duration, setDuration] = useState("")
  const [logNotes, setLogNotes] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getWorkoutLogs(clientId)
      .then((data) => setLogs(Array.isArray(data) ? data : []))
      .catch(() => setLogs([]))
  }, [clientId])

  async function handleAdd() {
    if (!routineId) return
    setSaving(true)
    try {
      const log = await createWorkoutLog(
        {
          routine_id: routineId,
          performed_at: new Date().toISOString().split("T")[0],
          duration_minutes: duration ? Number(duration) : undefined,
          notes: logNotes || undefined,
        },
        clientId,
      )
      setLogs((prev) => [log, ...prev])
      setDuration("")
      setLogNotes("")
      setShowForm(false)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Delete this workout log?")) return
    await deleteWorkoutLog(id)
    setLogs((prev) => prev.filter((l) => l.id !== id))
  }

  return (
    <div className="rounded-xl border border-white/10 bg-surface">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <h3 className="text-sm font-semibold text-white">
          Workout History <span className="text-muted font-normal">({logs.length} sessions)</span>
        </h3>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="text-xs text-primary hover:text-primary-hover transition"
        >
          + Log Workout
        </button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="px-4 py-3 border-b border-white/5 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <select
              value={routineId}
              onChange={(e) => setRoutineId(Number(e.target.value))}
              className="rounded-lg border border-white/10 bg-surface-light px-2 py-1.5 text-sm text-white outline-none focus:border-primary"
            >
              {routines.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <input
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="Duration (min)"
              className="rounded-lg border border-white/10 bg-surface-light px-2 py-1.5 text-sm text-white outline-none focus:border-primary"
            />
          </div>
          <input
            value={logNotes}
            onChange={(e) => setLogNotes(e.target.value)}
            placeholder="Notes (optional)"
            className="w-full rounded-lg border border-white/10 bg-surface-light px-2 py-1.5 text-sm text-white outline-none focus:border-primary"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={saving || routines.length === 0}
              className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-black hover:bg-primary-hover transition disabled:opacity-50"
            >
              {saving ? "Saving..." : "Log"}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="text-xs text-muted hover:text-white transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* History */}
      <div className="max-h-60 overflow-y-auto">
        {logs.length === 0 ? (
          <p className="px-4 py-4 text-xs text-muted text-center">No workouts logged yet.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {logs.map((l) => (
              <div key={l.id} className="flex items-center gap-3 px-4 py-2.5">
                <div className="flex-1">
                  <p className="text-sm text-white">{l.routine?.name ?? "Unknown routine"}</p>
                  <p className="text-xs text-muted">
                    {formatDate(l.performed_at)}
                    {l.duration_minutes ? ` · ${l.duration_minutes} min` : ""}
                    {l.notes ? ` · ${l.notes}` : ""}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(l.id)}
                  className="text-xs text-danger hover:text-danger/80"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Client Profile Page ──────────────────────────────────────────────────────

type ProfileTab = "overview" | "progress" | "notes"

export default function ClientProfilePage() {
  const { clientId } = useParams<{ clientId: string }>()
  const id = Number(clientId)
  const navigate = useNavigate()

  const [client, setClient] = useState<Client | null>(null)
  const [routines, setRoutines] = useState<Routine[]>([])
  const [logs, setLogs] = useState<WorkoutLog[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<ProfileTab>("overview")

  useEffect(() => {
    Promise.all([getClients(), getRoutines({ userId: id }), getWorkoutLogs(id)])
      .then(([clients, rts, wlogs]) => {
        setClient(clients.find((c) => c.id === id) ?? null)
        setRoutines(rts)
        setLogs(Array.isArray(wlogs) ? wlogs : [])
      })
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return <div className="text-muted p-6">Loading...</div>

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4">
        <Link to="/dashboard/training" className="text-muted hover:text-white transition">
          &larr; Back
        </Link>
        <div className="flex items-center gap-3 flex-1">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-dim text-lg font-bold text-primary">
            {client?.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2) ?? "?"}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">{client?.name ?? `Client #${id}`}</h2>
            <p className="text-xs text-muted">
              {client?.email}
              {client?.membership && (
                <span className="ml-2 capitalize">
                  &middot; {client.membership.plan} &middot;{" "}
                  <span
                    className={
                      client.membership.status === "active" ? "text-success" : "text-warning"
                    }
                  >
                    {client.membership.status}
                  </span>
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/dashboard/programs/${id}`)}
          className="shrink-0 rounded-lg bg-primary px-3 py-2 sm:px-4 text-xs sm:text-sm font-semibold text-black hover:bg-primary-hover transition"
        >
          Ver Programa
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className="rounded-xl border border-white/10 bg-surface p-3 sm:p-4 text-center">
          <p className="text-xs text-muted">RUTINAS</p>
          <p className="mt-1 text-2xl font-bold text-primary">{routines.length}</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-surface p-3 sm:p-4 text-center">
          <p className="text-xs text-muted">TIPO</p>
          <p className="mt-1 text-base sm:text-lg font-bold text-primary capitalize">
            {client?.client_type === "personal" ? "Personal" : "Grupal"}
          </p>
        </div>
        <div className="rounded-xl border border-white/10 bg-surface p-3 sm:p-4 text-center">
          <p className="text-xs text-muted">MIEMBRO DESDE</p>
          <p className="mt-1 text-sm sm:text-lg font-bold text-primary">
            {client?.membership ? formatDate(client.membership.start_date) : "—"}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-surface-elevated p-1 w-fit">
        {(["overview", "progress", "notes"] as ProfileTab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`rounded-md px-4 py-1.5 text-xs font-medium capitalize transition ${
              activeTab === tab ? "bg-primary text-black" : "text-muted hover:text-white"
            }`}
          >
            {tab === "overview" ? "Resumen" : tab === "progress" ? "Progreso" : "Notas de Sesión"}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "overview" && (
        <>
          <div className="grid gap-6 lg:grid-cols-2">
            <BodyMetricsSection clientId={id} />
            <WorkoutLogsSection clientId={id} routines={routines} />
          </div>
          <div className="rounded-xl border border-white/10 bg-surface">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-semibold text-white">Rutinas asignadas</h3>
              <Link
                to={`/dashboard/programs/${id}`}
                className="text-xs text-primary hover:text-primary/80 transition"
              >
                Editar
              </Link>
            </div>
            {routines.length === 0 ? (
              <p className="px-4 py-4 text-xs text-muted text-center">Sin rutinas asignadas.</p>
            ) : (
              <div className="divide-y divide-white/5">
                {routines.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="flex-1">
                      <p className="text-sm text-white">{r.name}</p>
                      <p className="text-xs text-muted">
                        {r.scheduled_day !== undefined && r.scheduled_day !== null
                          ? [
                              "Domingo",
                              "Lunes",
                              "Martes",
                              "Miércoles",
                              "Jueves",
                              "Viernes",
                              "Sábado",
                            ][r.scheduled_day]
                          : "Sin día asignado"}{" "}
                        &middot; {r.routine_exercises.length} ejercicios
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === "progress" && client && (
        <ProgressCharts clientId={id} clientName={client.name} />
      )}

      {activeTab === "notes" && (
        <div className="rounded-xl border border-white/10 bg-surface p-4">
          <h3 className="mb-4 text-sm font-semibold text-white uppercase tracking-wide">
            Notas de Sesión — {client?.name}
          </h3>
          <SessionNotes
            logs={logs}
            onNoteUpdated={(logId, notes) =>
              setLogs((prev) => prev.map((l) => (l.id === logId ? { ...l, notes } : l)))
            }
          />
        </div>
      )}
    </div>
  )
}
